import React, { useEffect, useState } from 'react'
import { Tooltip, IconButton, Theme, Badge } from '@material-ui/core'

import { Container } from './Container'
import { WorkSpaceUser } from '../models'
import { useHistory, useLocation } from 'react-router-dom'
import { isAccountManager, VENDOR } from '../helpers'
import { Routes } from '../navigation/config/routes'
import { navigationConfig } from '../navigation/config/navigationItems'
import {
    getActiveNavApp,
    setActiveNavApp,
} from '../navigation/utils/activeAppContext'

import VpnKeyIcon from '@material-ui/icons/VpnKey'

import { ReactComponent as EZTurnIcon } from '../assets/icons/ez-turn.svg'
import { ReactComponent as EZFolioLogo } from '../assets/icons/ez-folio.svg'
import { ReactComponent as EZManageLogo } from '../assets/icons/ez-manage.svg'
import { ReactComponent as GardLogo } from '../assets/icons/GARD.svg'
import { useSupportTicket } from '../hooks/useSupportTicket'
import { usePropertyTokens } from '../containers/Admin/Property/hooks/usePropertyTokens'
import { APPLICATION_SLUGS } from '../constants/applications'
import { useAppDispatch } from '../hooks'
import { setOrganization } from '../store'
import { useAddRemovePropertyForms } from '../hooks/useAddRemovePropertyForms'

interface AppSelectorProps {
    workspaceUser?: WorkSpaceUser
    theme: Theme
}

export const AppSelector = ({ workspaceUser, theme }: AppSelectorProps) => {
    const history = useHistory()
    const location = useLocation()
    const dispatch = useAppDispatch()

    const {
        pendingTicketCount,
        getPendingSupportTicketCount,
    } = useSupportTicket()

    const { getPendingFormCount } = useAddRemovePropertyForms(null)
    const [pendingFormCount, setPendingFormCount] = useState(0)

    const { tokenBalance } = usePropertyTokens(
        workspaceUser?.active_workspace.id.toString() || '',
    )

    const isDailyActivity = location.pathname.startsWith(
        Routes.apartment.dailyActivity.root,
    )

    const isEZTurnFromRoute = [
        ...navigationConfig.apartment.mainItems,
        ...navigationConfig.apartment.footerItems,
        ...navigationConfig.vendor.mainItems,
        ...navigationConfig.vendor.footerItems,
        ...navigationConfig.shared.mainItems,
        ...navigationConfig.shared.footerItems,
    ].some((item) => location.pathname.startsWith(item.link))

    const isEZFolioSelected = [
        ...navigationConfig.organization.mainItems,
        ...navigationConfig.organization.footerItems,
    ].some((item) => location.pathname.startsWith(item.link))

    const isAdminSelected = [
        ...navigationConfig.admin.mainItems,
        ...navigationConfig.admin.footerItems,
    ].some((item) => location.pathname.startsWith(item.link))

    const isEZManageFromRoute = [
        ...navigationConfig.manage.mainItems,
        ...navigationConfig.manage.footerItems,
    ].some((item) => location.pathname.startsWith(item.link))

    useEffect(() => {
        if (isDailyActivity) return
        if (isEZManageFromRoute) {
            setActiveNavApp('manage')
        } else if (isEZTurnFromRoute) {
            setActiveNavApp('ezturn')
        }
    }, [isDailyActivity, isEZManageFromRoute, isEZTurnFromRoute])

    const persistedApp = getActiveNavApp()
    const isEZTurnSelected = isDailyActivity
        ? persistedApp !== 'manage'
        : isEZTurnFromRoute
    const isEZManageSelected = isDailyActivity
        ? persistedApp === 'manage'
        : isEZManageFromRoute

    const showAdminButton = isAccountManager(workspaceUser)
    const [tokensRemaining, setTokensRemaining] = useState(0)

    // Get company applications
    const companyApplications =
        workspaceUser?.active_workspace.applications || []
    const applicationSlugs = new Set(
        companyApplications.map((app) => app.slug.toLowerCase()),
    )

    // Check if company has access to each application
    const hasEZTurn = applicationSlugs.has(APPLICATION_SLUGS.EZTURN)
    const hasEZFolio = applicationSlugs.has(APPLICATION_SLUGS.EZFOLIO)
    const hasEZManage = applicationSlugs.has(APPLICATION_SLUGS.EZMANAGE)
    const hasGard = applicationSlugs.has(APPLICATION_SLUGS.GARD)

    useEffect(() => {
        if (isAccountManager(workspaceUser)) {
            console.log('getPendingFormCount')
            getPendingSupportTicketCount()
            getPendingFormCount().then((count) => {
                setPendingFormCount(count)
            })
        }
    }, [])

    useEffect(() => {
        const tokensRemaining = tokenBalance?.current_balance || 0
        setTokensRemaining(tokensRemaining)
    }, [tokenBalance])

    const getBadgeColor = (tokens: number): string => {
        if (tokens > 3000) {
            return theme.palette.success?.main || '#4caf50' // Green
        } else if (tokens >= 500) {
            return theme.palette.warning?.main || '#ff9800' // Yellow
        } else {
            return theme.palette.error?.main || '#ef4444' // Red
        }
    }

    return (
        <Container>
            {hasEZTurn && (
                <Tooltip
                    title="EZTurn"
                    style={{ marginLeft: theme.spacing(1) }}
                >
                    <IconButton
                        size="small"
                        onClick={() => {
                            dispatch(setOrganization(null))
                            if (
                                workspaceUser?.active_workspace.company_type ===
                                VENDOR
                            ) {
                                history.push(Routes.vendor.ezwork)
                            } else {
                                history.push(Routes.apartment.schedule.root)
                            }
                        }}
                        style={{
                            borderRadius: 0,
                            width: 48,
                            height: 48,
                            borderBottom: isEZTurnSelected
                                ? `4px solid white`
                                : 'none',
                        }}
                    >
                        <EZTurnIcon style={{ width: 42, height: 42 }} />
                    </IconButton>
                </Tooltip>
            )}

            {hasEZFolio && (
                <Tooltip
                    title="EZFolio"
                    style={{ marginLeft: theme.spacing(1) }}
                >
                    <IconButton
                        size="small"
                        style={{
                            borderRadius: 0,
                            width: 48,
                            height: 48,
                            borderBottom: isEZFolioSelected
                                ? `4px solid white`
                                : 'none',
                        }}
                        onClick={() => {
                            const orgId =
                                workspaceUser?.active_workspace.organization
                            if (orgId != null) {
                                dispatch(setOrganization(Number(orgId)))
                            }
                            history.push(Routes.organization.jobBoard)
                        }}
                    >
                        <EZFolioLogo style={{ width: 42, height: 42 }} />
                    </IconButton>
                </Tooltip>
            )}

            {hasEZManage && (
                <Tooltip
                    title={`EZManage -- ${tokensRemaining} Tokens Remaining`}
                    style={{ marginLeft: theme.spacing(1) }}
                >
                    <IconButton
                        size="small"
                        style={{
                            borderRadius: 0,
                            width: 48,
                            height: 48,
                            borderBottom: isEZManageSelected
                                ? `4px solid white`
                                : 'none',
                        }}
                        onClick={() => {
                            history.push(Routes.manage.propertyEvents.root)
                        }}
                    >
                        <Badge
                            overlap="circle"
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            badgeContent={
                                <div
                                    style={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        backgroundColor: getBadgeColor(
                                            tokensRemaining,
                                        ),
                                        border: '2px solid white',
                                        boxSizing: 'border-box',
                                    }}
                                />
                            }
                        >
                            <EZManageLogo style={{ width: 42, height: 42 }} />
                        </Badge>
                    </IconButton>
                </Tooltip>
            )}

            {hasGard && (
                <Tooltip title="Gard" style={{ marginLeft: theme.spacing(1) }}>
                    <IconButton
                        size="small"
                        style={{
                            borderRadius: 0,
                            width: 48,
                            height: 48,
                        }}
                        onClick={() => {
                            window.open('https://www.gardapp.co/', '_blank')
                            return false
                        }}
                    >
                        <GardLogo style={{ width: 42, height: 42 }} />
                    </IconButton>
                </Tooltip>
            )}

            {showAdminButton && (
                <Tooltip title="Admin" style={{ marginLeft: theme.spacing(1) }}>
                    <IconButton
                        size="small"
                        style={{
                            borderRadius: 0,
                            width: 48,
                            height: 48,
                            borderBottom: isAdminSelected
                                ? `4px solid white`
                                : 'none',
                        }}
                        onClick={() => {
                            history.push(Routes.admin.organization.list)
                        }}
                    >
                        <Badge
                            badgeContent={pendingTicketCount + pendingFormCount}
                            color="error"
                        >
                            <VpnKeyIcon
                                style={{ width: 42, height: 42 }}
                                htmlColor="white"
                            />
                        </Badge>
                    </IconButton>
                </Tooltip>
            )}
        </Container>
    )
}
